import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  analyzeInsightWide,
  createInsightSession,
  getInsightStatus,
  getInsightGuidance,
  uploadInsightCapture,
  QUICK_WIDE_VIEW,
} from '../../utils/insightApi';
import { fileToJpegDataUrl } from '../../utils/imageFileUtils';
import { getSessionId, pathWithSessionId } from '../../utils/sessionManager';
import { hairAppPath, resolveInsightSessionId, setHairInsightSessionId } from '../../config/hairApp';
import { hairWidePreviewStorageKey } from '../../utils/reportCustomer';
import { resolveGuestUserDataForAnalysis } from '../../../../../enterprise/salon/utils/bootstrapFaceSalonSession';
import EnterpriseCameraSelect from '../../../../../enterprise/shared/EnterpriseCameraSelect';
import { parseCameraSelectValue, resolveFaceCameraSelectValue } from '../../../../../enterprise/shared/cameraSelectValues';
import {
  listMicroscopeVideoInputs,
  openBuiltinFaceCameraStream,
} from '../../../../../enterprise/shared/usbMicroscopeCamera';
import './HairInsight.css';

const REF_MALE = `${process.env.PUBLIC_URL || ''}/hair/capture-ref-male.png`;
const REF_FEMALE = `${process.env.PUBLIC_URL || ''}/hair/capture-ref-female.png`;

const FACING_USER = 'user';
const FACING_ENV = 'environment';

const CAPTURE_TIPS = [
  { id: 'center', label: 'Keep the head centered.' },
  { id: 'regions', label: 'Include Hairline, Top and Crown.' },
  { id: 'light', label: 'Avoid strong backlighting.' },
  { id: 'steady', label: 'Hold the device steady.' },
];

function IconBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

const DEFAULT_GUIDANCE = {
  overview: {
    title: 'Capture Hair',
    instructions: [
      'Fit the head inside the outline.',
      'Keep the hairline near the top of the guide.',
      'Keep the top of the head and crown visible.',
      'Hold the camera slightly above eye level.',
    ],
  },
  front: {
    title: 'Capture Hair',
    instructions: [
      'Fit the head inside the outline.',
      'Keep the hairline near the top of the guide.',
      'Keep the top of the head and crown visible.',
    ],
  },
  left: { title: 'Capture Left', instructions: ['Show left profile'] },
  right: { title: 'Capture Right', instructions: ['Show right profile'] },
  top: { title: 'Capture Crown / Top', instructions: ['Camera slightly above the head'] },
  back: { title: 'Capture Back', instructions: ['Capture rear coverage'] },
};

function positionHintFromQuality(quality) {
  if (!quality) return 'Center the head and keep Hairline, Top and Crown visible.';
  const hints = quality.regionHints || {};
  const issues = quality.issues || [];
  if (quality.grade === 'unusable') return 'The image is not clear enough. Please retake.';
  if (issues.some((i) => /dark/i.test(i))) return 'Move closer to better light';
  if (hints.hairline === 'visible' && hints.top === 'visible') {
    if (hints.crown === 'limited') return 'Hairline ✓ · Top ✓ · Crown limited — you can continue';
    return 'Good to capture';
  }
  if (hints.hairline !== 'visible') return 'Move slightly up — keep hairline in the top band';
  if (hints.top !== 'visible') return 'Move slightly down — keep top of head visible';
  return 'Center the head and keep Hairline, Top and Crown visible.';
}

export default function HairInsightWideCapture() {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const scanMode = location.state?.scanMode || 'advanced';

  const [sessionId, setSessionId] = useState(() => resolveInsightSessionId(location, null));
  const [quickView, setQuickView] = useState(QUICK_WIDE_VIEW);
  const [guidance, setGuidance] = useState(DEFAULT_GUIDANCE);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [error, setError] = useState(null);
  const [quality, setQuality] = useState(null);
  const [liveHint, setLiveHint] = useState(
    'Center the head and keep Hairline, Top and Crown visible.',
  );
  const [facingMode, setFacingMode] = useState(FACING_USER);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [cameraActive, setCameraActive] = useState(false);

  const view = quickView;

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  const startCameraFromSelect = useCallback(
    async (rawValue) => {
      stopStream();
      const parsed = parseCameraSelectValue(rawValue);
      if (parsed.kind === 'none') {
        setCameraActive(false);
        return;
      }
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Camera unavailable. Please check camera permissions.');
          return;
        }
        let stream;
        if (parsed.kind === 'device') {
          setSelectedDeviceId(parsed.deviceId);
          setFacingMode(FACING_USER);
          stream = await openBuiltinFaceCameraStream({
            deviceId: parsed.deviceId,
            facing: 'user',
            timeoutMs: 15000,
            width: 1280,
            height: 960,
          });
        } else {
          setSelectedDeviceId('');
          const facing = parsed.facing === 'rear' ? FACING_ENV : FACING_USER;
          setFacingMode(facing);
          stream = await openBuiltinFaceCameraStream({
            facing,
            timeoutMs: 15000,
            width: 1280,
            height: 960,
          });
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraActive(true);
        setLiveHint('Fit the head inside the outline');
        setError(null);
      } catch {
        setError('Camera unavailable. Please check camera permissions.');
        setCameraActive(false);
      }
    },
    [stopStream],
  );

  const cameraSelectValue = resolveFaceCameraSelectValue({
    deviceId: selectedDeviceId,
    facing: facingMode === FACING_ENV ? 'rear' : 'front',
  });

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    setSessionBusy(true);
    setError(null);
    try {
      const guest = resolveGuestUserDataForAnalysis(location.state);
      const created = await createInsightSession(guest.name || location.state?.userData?.name || 'GUEST', {
        scanMode,
        salonReference: getSessionId() || undefined,
        customerName: guest.name || location.state?.userData?.name,
        checkinId: location.state?.checkinId || location.state?.checkin_id,
      });
      const id = created?.session?.sessionId;
      if (!id) throw new Error('Session was not created. Please retry.');
      setSessionId(id);
      setHairInsightSessionId(id);
      return id;
    } catch (e) {
      setError(e.message || 'Could not start session');
      throw e;
    } finally {
      setSessionBusy(false);
    }
  }, [sessionId, location.state, scanMode]);

  useEffect(() => {
    const resolved = resolveInsightSessionId(location, null);
    if (resolved) {
      setSessionId(resolved);
      setHairInsightSessionId(resolved);
    }
  }, [location.search, location.state?.insightSessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getInsightStatus();
        if (!cancelled && Array.isArray(status.wideViews)) {
          // Old backends reject "overview" — fall back to front for Quick Scan.
          if (!status.wideViews.includes(QUICK_WIDE_VIEW)) {
            setQuickView('front');
          }
        }
      } catch {
        if (!cancelled) setQuickView('front');
      }
      try {
        const g = await getInsightGuidance();
        if (!cancelled && g.wide) setGuidance({ ...DEFAULT_GUIDANCE, ...g.wide });
      } catch {
        /* defaults */
      }
      if (!sessionId && !cancelled) {
        try {
          await ensureSession();
        } catch {
          /* shown */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.insightSessionId]);

  useEffect(() => {
    if (preview) {
      stopStream();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const cams = await listMicroscopeVideoInputs();
        if (!cancelled) setVideoDevices(cams);
      } catch {
        /* ignore */
      }
      if (!cancelled) {
        const initial = resolveFaceCameraSelectValue({
          deviceId: selectedDeviceId,
          facing: facingMode === FACING_ENV ? 'rear' : 'front',
        });
        await startCameraFromSelect(initial || 'auto:front');
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const handleCameraSelectChange = useCallback(
    async (value) => {
      if (preview) return;
      await startCameraFromSelect(value);
    },
    [preview, startCameraFromSelect],
  );

  const refreshVideoDevices = useCallback(async () => {
    try {
      const cams = await listMicroscopeVideoInputs();
      setVideoDevices(cams);
    } catch {
      /* ignore */
    }
  }, []);

  const snapFromVideo = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facingMode === FACING_USER) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const saveCapture = async (dataUrl, viewOverride) => {
    if (!dataUrl) {
      setError('No image to save.');
      return null;
    }
    setBusy(true);
    setError(null);
    const useView = viewOverride || view;
    try {
      const sid = sessionId || (await ensureSession());
      const res = await uploadInsightCapture(sid, {
        source: 'wide_camera',
        view: useView,
        image: dataUrl,
      });
      setQuality(res.quality || null);
      setLiveHint(positionHintFromQuality(res.quality));
      setPreview(dataUrl);
      return res;
    } catch (e) {
      const msg = e.message || 'Capture failed';
      // Compatibility: backend still on pre-overview schema
      if (
        scanMode === 'advanced' &&
        useView === QUICK_WIDE_VIEW &&
        /view must be one of/i.test(msg)
      ) {
        setQuickView('front');
        try {
          const sid = sessionId || (await ensureSession());
          const res = await uploadInsightCapture(sid, {
            source: 'wide_camera',
            view: 'front',
            image: dataUrl,
          });
          setQuality(res.quality || null);
          setLiveHint(positionHintFromQuality(res.quality));
          setPreview(dataUrl);
          return res;
        } catch (e2) {
          setError(e2.message || msg);
          return null;
        }
      }
      setError(msg);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onCapture = async () => {
    const dataUrl = snapFromVideo();
    if (!dataUrl) {
      setError('Camera not ready — try upload instead.');
      return;
    }
    await saveCapture(dataUrl);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const dataUrl = await fileToJpegDataUrl(file, { maxSide: 1600, quality: 0.9 });
      await saveCapture(dataUrl);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const onRetake = () => {
    setPreview(null);
    setQuality(null);
    setError(null);
  };

  const goMicroscope = (sid, wide = {}) => {
    if (sid && preview) {
      try {
        sessionStorage.setItem(hairWidePreviewStorageKey(sid), preview);
      } catch {
        /* quota / private mode */
      }
    }
    navigate(pathWithSessionId('/insight/microscope', sid), {
      state: {
        insightSessionId: sid,
        scanMode,
        captureRecommendation: wide.captureRecommendation || {
          needsAdditionalCapture: true,
          source: 'microscope',
          target: 'hairline',
          reason: 'Capture a close scalp look at the hairline.',
          priority: 'high',
          step: 1,
          totalSteps: 3,
        },
        recommendedTarget:
          wide.recommendedTarget ||
          wide.captureRecommendation?.target ||
          'hairline',
        coverageMap: wide.coverageMap,
        token: location.state?.token,
        wideImageUrl: preview,
      },
    });
  };

  const onContinue = async () => {
    if (!preview) {
      setError('Capture a hair image first.');
      return;
    }
    if (quality?.grade === 'unusable') {
      setError('The image is not clear enough. Please retake the capture.');
      return;
    }
    setError(null);
    try {
      const sid = sessionId || (await ensureSession());
      const isAdvancedFlow = scanMode === 'advanced' || scanMode === 'detailed';
      if (isAdvancedFlow) {
        goMicroscope(sid, {});
        return;
      }
      setAnalyzing(true);
      try {
        const wide = await analyzeInsightWide(sid);
        goMicroscope(sid, wide);
      } catch (e) {
        if (/404|not found|failed \(404\)|analyze-wide/i.test(e.message || '')) {
          goMicroscope(sid, {});
          return;
        }
        throw e;
      }
    } catch (e) {
      setError(e.message || 'Analysis is temporarily unavailable. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const gradeClass =
    quality?.grade === 'unusable' ? 'warn' : quality?.grade === 'limited' ? 'warn' : 'ok';

  return (
    <div className="hair-insight-page page-bg hi-capture-first hi-wide-capture">
      <header className="hi-header hi-wide-capture__header">
        <button
          type="button"
          className="hi-wide-capture__icon-btn hi-wide-capture__back"
          aria-label="Back"
          onClick={() => navigate(hairAppPath('/landing'))}
        >
          <IconBack />
        </button>
        <div className="hi-progress-steps hi-wide-capture__steps" aria-label="Progress">
          <span className="is-on">1. Hair Scan</span>
          <span>2. Scalp Detail</span>
          <span>3. Results</span>
        </div>
      </header>

      <div className="hi-layout hi-layout-capture hi-wide-capture__layout">
        <section className="hi-preview-card hi-wide-capture__preview">
          <div className="hi-preview-frame hi-wide-capture__frame">
            {preview ? (
              <img src={preview} alt="Captured hair" className="hi-preview-media" />
            ) : (
              <video
                ref={videoRef}
                className={`hi-preview-media is-live ${facingMode === FACING_USER ? 'is-mirror' : ''}`}
                playsInline
                muted
                autoPlay
              />
            )}
            {!preview ? (
              <div className="hi-wide-capture__camera-dock">
                <EnterpriseCameraSelect
                  label="Camera"
                  value={cameraSelectValue}
                  devices={videoDevices}
                  onChange={handleCameraSelectChange}
                  onRefresh={refreshVideoDevices}
                  disabled={busy || analyzing}
                />
              </div>
            ) : null}
            {/* No region guide lines on live preview — clean camera only */}
            <div className="hi-wide-capture__corners" aria-hidden>
              <i />
              <i />
              <i />
              <i />
            </div>
            <div className="hi-wide-capture__prompt" role="status">
              <span className="hi-wide-capture__prompt-ico" aria-hidden>
                ✺
              </span>
              <span>{liveHint}</span>
            </div>
          </div>
          <p className="hi-live-hint hi-wide-capture__status" role="status">
            <span className="hi-wide-capture__ready-dot" aria-hidden />
            {preview ? 'Photo ready — review and continue' : cameraActive ? 'Camera ready' : 'Starting camera…'}
            <span className="hi-wide-capture__status-sep">·</span>
            {preview ? 'Tap Continue when the framing looks good.' : 'Position the customer and tap Capture.'}
          </p>
        </section>

        <aside className="hi-guide-card hi-wide-capture__guide">
          <h2>What we&apos;re capturing</h2>
          <p className="hi-wide-capture__guide-lede">
            Take one top-down hair photo that shows the hairline, top of the head, and crown in the
            same frame — match the reference examples.
          </p>

          <div className="hi-wide-capture__refs">
            <p className="hi-wide-capture__refs-label">
              <span aria-hidden>💡</span> Reference
            </p>
            <div className="hi-wide-capture__ref-grid">
              <figure className="hi-wide-capture__ref">
                <img src={REF_MALE} alt="Male hair capture reference" />
                <figcaption>Male reference</figcaption>
              </figure>
              <figure className="hi-wide-capture__ref">
                <img src={REF_FEMALE} alt="Female hair capture reference" />
                <figcaption>Female reference</figcaption>
              </figure>
            </div>
          </div>

          {quality && (
            <div className={`hi-quality ${gradeClass}`}>
              <strong>
                {quality.grade === 'unusable'
                  ? 'Retake needed'
                  : quality.grade === 'limited'
                    ? 'Good enough to continue'
                    : 'Good image'}
              </strong>
              <ul>
                {quality.regionHints?.hairline === 'visible' && <li>✓ Hairline visible</li>}
                {quality.regionHints?.top === 'visible' && <li>✓ Top visible</li>}
                {quality.regionHints?.crown === 'visible' && <li>✓ Crown visible</li>}
                {quality.regionHints?.crown === 'limited' && <li>Crown view is limited</li>}
                {(quality.issues || []).slice(0, 3).map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </div>
          )}
          {error && <p className="hi-error">{error}</p>}
          {sessionBusy && <p className="hi-muted">Starting session…</p>}
          {analyzing && <p className="hi-muted">Checking your hair…</p>}
        </aside>

        <section className="hi-wide-capture__tips" aria-label="Tips for best results">
          <h3>Tips for best results</h3>
          <ul>
            {CAPTURE_TIPS.map((tip) => (
              <li key={tip.id}>
                <span className={`hi-wide-capture__tip-ico hi-wide-capture__tip-ico--${tip.id}`} aria-hidden />
                <span>{tip.label}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="hi-actions hi-actions-capture hi-wide-capture__actions">
        {preview ? (
          <button type="button" className="hi-btn ghost" disabled={busy || analyzing} onClick={onRetake}>
            Retake
          </button>
        ) : null}
        {!preview ? (
          <button
            type="button"
            className="hi-btn primary hi-btn-capture"
            disabled={busy || sessionBusy || analyzing}
            onClick={onCapture}
          >
            {busy ? 'Saving…' : 'Capture Hair'}
          </button>
        ) : (
          <button
            type="button"
            className="hi-btn primary hi-btn-capture"
            disabled={busy || analyzing || quality?.grade === 'unusable'}
            onClick={onContinue}
          >
            {analyzing ? 'Checking…' : 'Continue'}
          </button>
        )}
        <button
          type="button"
          className="hi-btn ghost hi-wide-capture__upload"
          disabled={busy || analyzing}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload Photo Instead
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onUpload}
        />
      </div>
    </div>
  );
}
